import {
  GridColDef,
  GridInitialState,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import React from "react";
import { Link } from "react-router-dom";
import useApi from "../../hooks/useGaneshApi";
import { Delivery_T } from "../../types/Delivery";
import GaneshDataGrid from "../DataGrid/GaneshDataGrid";
import DisplayDatasetSchemaModalButton from "../Schema/DisplayDatasetSchemaModalButton";
import { useQuery } from "@tanstack/react-query";
import { getDateValue, renderDateCell, renderNumberCell } from "utils/dataGrid";
import JsonDisplayModalWithFetch from "../Compliance/UI/JsonDisplayModalWithFetch";
import { useError } from "../../contexts/ErrorContext";

interface DeliveriesTableProps {
  customerId?: string;
}
const defaultGridState: GridInitialState = {
  filter: {
    filterModel: {
      items: [
        {
          id: 1,
          field: "is_active",
          operator: "is",
          value: "true",
        },
      ],
    },
  },
  sorting: { sortModel: [{ field: "next_delivery", sort: "asc" }] },
  pagination: { paginationModel: { pageSize: 25, page: 0 } },
  pinnedColumns: { left: [] },
  columns: { columnVisibilityModel: {} },
  density: "compact",
};

const DeliveriesTable: React.FC<DeliveriesTableProps> = ({ customerId }) => {
  const { apiRequest } = useApi();
  const { showError } = useError();

  const endpoint = customerId
    ? `/v1/customers/${customerId}/deliveries`
    : "/v1/deliveries";

  const {
    data: deliveries,
    isLoading,
    isError,
    error,
  } = useQuery<Delivery_T[], Error>({
    queryKey: ["deliveries", customerId || "all"],
    queryFn: async () => {
      const response = await apiRequest<Delivery_T[]>(endpoint);
      return response.data || [];
    },
  });

  if (isError) {
    showError(`Failed to fetch deliveries: ${error.message}`);
  }

  const columns_def: GridColDef[] = [
    {
      field: "id",
      headerName: "ID",
      width: 90,
      renderCell: (params: GridRenderCellParams<Number>) => (
        <Link to={`/delivery/${params.value}`}>{params.value}</Link>
      ),
    },
    {
      field: "most_recent_export_id",
      headerName: "Export Summary",
      width: 110,
      renderCell: (params: GridRenderCellParams) => (
        <JsonDisplayModalWithFetch
          fetchUrl={`/v1/exports/${params.value}/export_summary`}
          title="Export Summary"
          iconOnly={true}
          enableCollapse={true}
        />
      ),
    },
    {
      field: "schema_config",
      headerName: "Schema Config",
      width: 110,
      renderCell: (params: GridRenderCellParams) => (
        <DisplayDatasetSchemaModalButton
          datasetId={params.row.id}
          datasetName={params.row.delivery_name}
          datasetType="delivery"
        />
      ),
    },
    {
      field: "delivery_name",
      headerName: "Delivery Name",
      width: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Link to={`/delivery/${params.row.id}`}>{params.value}</Link>
      ),
    },
    {
      field: "customer_name",
      headerName: "Customer",
      width: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Link to={`/customer/${params.row.customer_id}/deliveries`}>
          {params.row.customer_name}
        </Link>
      ),
    },
    { field: "country_name", headerName: "Country", width: 200 },
    {
      field: "most_recent_volume",
      headerName: "Last Shipped Volume",
      width: 150,
      renderCell: renderNumberCell,
    },
    {
      field: "most_recent_delivery_date",
      headerName: "Most Recent Delivery",
      type: "date",
      width: 150,
      valueGetter: getDateValue,
      renderCell: renderDateCell,
    },
    {
      field: "next_delivery_date",
      headerName: "Next Delivery",
      type: "date",
      width: 150,
      valueGetter: getDateValue,
      renderCell: renderDateCell,
    },
    {
      field: "first_delivery_date",
      headerName: "First Delivery Date",
      type: "date",
      width: 200,
      valueGetter: getDateValue,
      renderCell: renderDateCell,
    },
    {
      field: "final_scheduled_delivery",
      headerName: "Final Scheduled Delivery",
      width: 150,
      valueGetter: getDateValue,
      renderCell: renderDateCell,
    },
    {
      field: "license_end_date",
      headerName: "License End Date",
      type: "date",
      width: 150,
      valueGetter: getDateValue,
      renderCell: renderDateCell,
    },
    {
      field: "count_completed_fulfillments",
      headerName: "Count of Completed Fulfillments",
      width: 200,
      renderCell: renderNumberCell,
    },
    {
      field: "count_fulfillments_on_schedule",
      headerName: "Count Fulfillments Remaining",
      width: 200,
      renderCell: renderNumberCell,
    },
    {
      field: "count_volume_upgrades_on_schedule",
      headerName: "Count Volume Upgrades Remaining",
      width: 200,
      renderCell: renderNumberCell,
    },
    {
      field: "next_volume_upgrade",
      headerName: "Next Volume Upgrade",
      type: "date",
      width: 150,
      valueGetter: getDateValue,
      renderCell: renderDateCell,
    },
    {
      field: "final_scheduled_volume_upgrade",
      headerName: "Final Scheduled Volume Upgrade",
      width: 150,
      valueGetter: getDateValue,
      renderCell: renderDateCell,
    },
    {
      field: "fulfillment_groups",
      headerName: "Fulfillment Group(s)",
      width: 300,
    },
    {
      field: "fulfillment_destination",
      headerName: "Fulfillment Destination",
      width: 300,
    },
    {
      field: "is_active",
      headerName: "Is Active",
      width: 150,
      type: "boolean",
    },
  ];
  // Conditionally remove the customer column if a customerId is provided
  const finalColumns = customerId
    ? columns_def.filter((col) => col.field !== "customer_name")
    : columns_def;

  return (
    <GaneshDataGrid
      gridId={"all-deliveries-table"}
      data={deliveries || []}
      columns={finalColumns}
      initialState={defaultGridState}
      loading={isLoading}
    />
  );
};

export default DeliveriesTable;
